import React, { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { ReactComponent as ScheduleIcon } from '../../assets/svg/ScheduleIcon.svg'
import { ReactComponent as Calendar } from '../../assets/svg/CalendarIcon.svg'
import { ReactComponent as Save } from '../../assets/svg/Save.svg'
import { ReactComponent as LockIcon } from '../../assets/svg/LockIcon.svg'
import { localeMap } from '../../constants/Constants'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { TextField } from '@mui/material'
import i18n from '../../i18n'
import Switcher from '../content/Switcher'
import PickTimeHorizontalModule from './PickTimeHorizontalModule'
import Loading from '../Loading'
import { format } from 'date-fns'

function ScheduleEmailModal({ isOpen, onClose, emailSchedulingData, updateEmailSchedulingData, onSchedule }) {
    const { t } = useTranslation()
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [returnHour, setReturnHour] = useState(emailSchedulingData.time?.hours || 8)
    const [returnMinute, setReturnMinute] = useState(emailSchedulingData.time?.minutes || 0)
    const [progress, setProgress] = useState(false)

    // Update parent state when hour/minute changes
    useEffect(() => {
        const date = new Date(emailSchedulingData.date)
        date.setHours(returnHour)
        date.setMinutes(returnMinute)
        date.setSeconds(0)
        date.setMilliseconds(0)
        updateEmailSchedulingData('date', date)
    }, [returnHour, returnMinute, isOpen])

    useEffect(() => {
        if (isOpen) {
            setIsDatePickerOpen(false)
            const roundedDate = new Date(emailSchedulingData.date)
            roundedDate.setSeconds(0)
            roundedDate.setMilliseconds(0)
            const minutesDif = 5 - (roundedDate.getMinutes() % 5)
            if (minutesDif !== 0) {
                roundedDate.setMinutes(roundedDate.getMinutes() + minutesDif)
            }
            setReturnHour(roundedDate.getHours())
            setReturnMinute(roundedDate.getMinutes())
        }
    }, [isOpen])

    const handlePublish = async () => {
        setProgress(true)
        try {
            console.log('Scheduling email with data:', emailSchedulingData)
            await onSchedule()
        } catch (error) {
            console.error('Error scheduling email:', error)
        } finally {
            setProgress(false)
        }
    }

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Schedule Email Modal"
            ariaHideApp={false}
            style={{
                overlay: {
                    zIndex: 100,
                },
                content: {
                    inset: 0,
                    padding: 0,
                    margin: 'auto',
                    marginTop: 40,
                    opacity: 1,
                    width: 700,
                    backgroundColor: 'transparent',
                    maxHeight: '92vh',
                    border: 'none',
                    borderRadius: 10,
                    boxShadow: 'none',
                    overflow: 'hidden',
                    position: 'relative',
                },
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div className="day-reflection-modal-icon" onClick={onClose}>
                    <CloseModalIcon />
                </div>
            </div>

            <div className="mt-[30px] rounded-[10px] bg-whiteContrast">
                {/* Header */}
                <div className="pt-[49px] pb-[22px] rounded-[10px] bg-lightBG relative">
                    <h2 className="text-xl font-bold text-deepPurple text-center">{t('Schedule email for new users')}</h2>
                    <p className="text-mudPurple text-center mt-2">{t('This tool helps you to schedule emails for users based on their timezone')}</p>
                </div>

                {/* Content */}
                <div className="max-h-[calc(92vh-160px)] overflow-auto grey-small-scroll p-6 space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-deepPurple mb-4">{t('Schedule')}</h3>

                        {/* Target Group */}
                        <div className="mb-4">
                            <label className="block text-mudPurple text-sm font-medium mb-2">{t('Target group')}</label>
                            <div className="flex gap-2">
                                <button
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        emailSchedulingData.targetGroup === 'Teachers' ? 'bg-deepPurple text-white' : 'bg-lightBG text-mudPurple hover:bg-containerGray'
                                    }`}
                                    onClick={() => updateEmailSchedulingData('targetGroup', 'Teachers')}
                                >
                                    {t('Teachers')}
                                </button>
                                <button
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        emailSchedulingData.targetGroup === 'Students' ? 'bg-deepPurple text-white' : 'bg-lightBG text-mudPurple hover:bg-containerGray'
                                    }`}
                                    onClick={() => updateEmailSchedulingData('targetGroup', 'Students')}
                                >
                                    {t('Students')}
                                </button>
                            </div>
                        </div>

                        {/* User Selection */}
                        <div className="mb-4">
                            <label className="block text-mudPurple text-sm font-medium mb-2">
                                {emailSchedulingData.targetGroup === 'Teachers' ? t('Select Teachers') : t('Select Students')}
                            </label>
                            <div className="flex gap-2">
                                <button
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        emailSchedulingData.userSelection === 'all' ? 'bg-deepPurple text-white' : 'bg-lightBG text-mudPurple hover:bg-containerGray'
                                    }`}
                                    onClick={() => updateEmailSchedulingData('userSelection', 'all')}
                                >
                                    {t('All')} {emailSchedulingData.targetGroup}
                                </button>
                                <button
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        emailSchedulingData.userSelection === 'new' ? 'bg-deepPurple text-white' : 'bg-lightBG text-mudPurple hover:bg-containerGray'
                                    }`}
                                    onClick={() => updateEmailSchedulingData('userSelection', 'new')}
                                >
                                    {t('New')} {emailSchedulingData.targetGroup}
                                </button>
                            </div>
                            <p className="text-xs text-mudPurple mt-2">
                                {emailSchedulingData.userSelection === 'all'
                                    ? t(`Send email to all ${emailSchedulingData.targetGroup.toLowerCase()}`)
                                    : t(`Send email only to newly added ${emailSchedulingData.targetGroup.toLowerCase()}`)}
                            </p>
                        </div>

                        <div className="flex flex-row gap-[10px]">
                            {/* Date */}
                            <div className="mb-4 flex-1">
                                <label className="block text-mudPurple text-sm font-medium mb-2">{t('Set date')}:</label>
                                <div className="relative">
                                    <div
                                        id="start-date-button"
                                        className="rounded-full pointer gap-[4px] flex flex-row bg-lightBG text-deselectPurple font-semibold text-base items-center justify-center h-[41px] px-[20px]"
                                        onClick={() => setIsDatePickerOpen(true)}
                                    >
                                        <Calendar />
                                        <p className="text-base text-deepPurple font-semibold"> {format(emailSchedulingData.date, 'dd.MM.yyyy')}</p>
                                    </div>
                                    <div style={{ position: 'absolute', top: 0, left: 65, visibility: 'hidden' }}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[i18n.language]}>
                                            <DatePicker
                                                open={isDatePickerOpen}
                                                onClose={() => setIsDatePickerOpen(false)}
                                                label="Date desktop"
                                                inputFormat="MM/dd/yyyy"
                                                value={emailSchedulingData.date ?? new Date()}
                                                onChange={(newValue) => {
                                                    if (newValue) {
                                                        const date = new Date(emailSchedulingData.date)
                                                        date.setFullYear(newValue.getFullYear())
                                                        date.setMonth(newValue.getMonth())
                                                        date.setDate(newValue.getDate())
                                                        updateEmailSchedulingData('date', date)
                                                    }
                                                }}
                                                minDate={new Date()}
                                                renderInput={(params) => <TextField {...params} />}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                            </div>

                            {/* Time */}
                            <div className="mb-4 flex-1">
                                <label className="block text-mudPurple text-sm font-medium mb-2">{t('Set time')}:</label>
                                <div className="flex items-center justify-center gap-2 bg-lightBG rounded-full   h-[41px]">
                                    <PickTimeHorizontalModule hour={returnHour} minute={returnMinute} setReturnMinute={setReturnMinute} setReturnHour={setReturnHour} />
                                </div>
                            </div>
                        </div>

                        {/* Label */}
                        <div className="mb-4">
                            <label className="block text-mudPurple text-sm font-medium mb-2">{t('Set label (for internal use)')}:</label>
                            <input
                                type="text"
                                placeholder={t('Label (optional)')}
                                value={emailSchedulingData.label}
                                onChange={(e) => updateEmailSchedulingData('label', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-deepPurple"
                            />
                        </div>

                        {/* Additional Options */}
                        <div className="space-y-4">
                            <h4 className="text-deepPurple font-semibold text-lg">{t('Additional Options')}</h4>

                            {emailSchedulingData.targetGroup !== 'Teachers' && (
                                <div className="flex flex-row justify-between items-center my-[7px] h-[40px] gap-[10px]">
                                    <p className="text-mudPurple text-lg font-medium flex-1">{t('Save to master list')}</p>
                                    <Switcher setSwitcher={(checked) => updateEmailSchedulingData('saveToMasterlist', checked)} switcher={emailSchedulingData.saveToMasterlist} />
                                </div>
                            )}

                            <div className="flex flex-row justify-between items-center my-[7px] h-[40px] gap-[10px]">
                                <p className="text-mudPurple text-lg font-medium flex-1">{t('Turn off new password prompt at login')}</p>
                                <Switcher
                                    setSwitcher={(checked) => updateEmailSchedulingData('disablePasswordPrompt', checked)}
                                    switcher={emailSchedulingData.disablePasswordPrompt}
                                />
                            </div>
                        </div>

                        {/* Schedule Info */}
                        <div className="bg-lightBG rounded-lg p-4 mt-6">
                            <h4 className="text-deepPurple font-semibold mb-2">{t('Schedule Summary')}</h4>
                            <div className="space-y-2 text-sm text-mudPurple">
                                <p>
                                    <strong>{t('Target')}:</strong> {emailSchedulingData.userSelection === 'all' ? t('All') : t('New')} {emailSchedulingData.targetGroup}
                                </p>
                                {emailSchedulingData.label && (
                                    <p>
                                        <strong>{t('Label')}:</strong> {emailSchedulingData.label}
                                    </p>
                                )}
                                <p className="text-mudPurple">
                                    {t('Email will be sent on')} <strong>{format(emailSchedulingData.date, 'dd.MM.yyyy')}</strong> {t('at')}{' '}
                                    <strong>
                                        {String(returnHour).padStart(2, '0')}:{String(returnMinute).padStart(2, '0')}
                                    </strong>
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                        <button
                            id="publish-button"
                            className={`pointer rounded-full py-3 flex justify-center min-w-[334px] bg-deepPurple text-highlightColor items-center gap-3 deepPurple font-semibold text-lg transition-all duration-300 ${
                                progress ? '' : 'hover:bg-highlightColor hover:text-deepPurple'
                            }`}
                            onClick={handlePublish}
                        >
                            {progress ? (
                                <Loading />
                            ) : (
                                <>
                                    <ScheduleIcon className="w-4 h-4" />
                                    {t('Schedule')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </ReactModal>
    )
}

export default ScheduleEmailModal
